import { Component, ElementRef, HostListener, Input, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { filter, map, Observable } from 'rxjs';
import {
  IonHeader,
  IonToolbar,
  IonButtons,
  IonButton,
  IonIcon,
  IonList,
  IonItem,
  IonLabel,
} from '@ionic/angular/standalone';
import { addIcons } from 'ionicons';
import {
  homeOutline,
  schoolOutline,
  peopleOutline,
  briefcaseOutline,
  informationCircleOutline,
  logInOutline,
  logOutOutline,
  personCircleOutline,
  settingsOutline,
  menu,
  chevronDownOutline,
  chevronUpOutline,
  close,
  personOutline
} from 'ionicons/icons';
import { TranslateService } from 'src/assets/translate.service';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { AuthService } from '../../services/auth.service';
import { ModalService } from '../../services/modal.service';
import { SessionService } from '../../services/session.service';
import { FilterStateService } from '../../services/filter-state.service';
import { allSections } from '../../constants/constants';
import { AdminStateService } from '../../services/admin-state.service';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    TranslatePipe,
    IonHeader,
    IonToolbar,
    IonButtons,
    IonButton,
    IonIcon,
    IonList,
    IonItem,
    IonLabel,
  ],
})
export class HeaderComponent implements OnInit {
  @Input() isNativeApp = false;
  @ViewChild('dropdownContainer', { static: false })
  dropdownContainer!: ElementRef;

  @ViewChild('mobileOpportunitiesToggle', { read: ElementRef, static: false })
  mobileOpportunitiesToggle!: ElementRef;

  @ViewChild('userMenuContainer', { static: false }) userMenuContainer!: ElementRef;
  @ViewChild('adminMenuContainer', { static: false }) adminMenuContainer!: ElementRef;
  @ViewChild('mobileAdminToggle', { read: ElementRef, static: false })

  mobileAdminToggle!: ElementRef;

  @ViewChild('mobileMenu', { static: false }) mobileMenu!: ElementRef;
  @ViewChild('mobileMenuToggle', { read: ElementRef, static: false }) mobileMenuToggle!: ElementRef;


  isLoggedIn$: Observable<boolean>;
  isAdmin$: Observable<boolean>;
  availableAdminSections$: Observable<Array<{ name: string; role: string; }>>;
  remainingTime$: Observable<string>;

  isOpportunitiesOpen = false;
  isMobileMenuOpen = false;
  isUserMenuOpen = false;
  isAdminMenuOpen = false;
  isOnProfilePage = false;

  opportunitiesDropdownPosition = { left: 0, top: 0 };
  adminDropdownPosition = { left: 0, top: 0 };

  public opportunityPages = [
    { title: 'STUDENTS', url: '/opportunity', filter: 'student' },
    { title: 'ACADEMIC_STAFF', url: '/opportunity', filter: 'academic-staff' },
    { title: 'ADMINISTRATIVE_STAFF', url: '/opportunity', filter: 'administrative-staff' },
    { title: 'NON_UNIVERSITY_STAKEHOLDER', url: '/opportunity', filter: 'non-university-stakeholder' },
  ];

  constructor(
    private authService: AuthService,
    public router: Router,
    public translate: TranslateService,
    private modalService: ModalService,
    private sessionService: SessionService,
    private filterStateService: FilterStateService,
    private adminStateService: AdminStateService
  ) {
    addIcons({
      homeOutline, schoolOutline, peopleOutline, briefcaseOutline,
      informationCircleOutline, logInOutline, logOutOutline, personCircleOutline,
      settingsOutline, menu, chevronDownOutline, chevronUpOutline, close,
      personOutline
    });

    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: any) => {
      this.isOnProfilePage = event.urlAfterRedirects.startsWith('/profile');
    });

    this.isLoggedIn$ = this.authService.isLoggedIn$;
    this.isAdmin$ = this.authService.userRoles$.pipe(
      map(roles => roles.includes('super-admin') || roles.includes('org-admin'))
    );

    this.availableAdminSections$ = this.authService.logCurrentUserRoles().pipe(
      map(roles => {
        const isSuperAdmin = roles.includes('super-admin');
        if (isSuperAdmin) {
          return allSections;
        }

        let sections = allSections.filter(section => roles.includes(section.role));

        if (roles.includes('org-admin')) {
          const superAdminSection = allSections.find(s => s.name === 'Super Admin');
          if (superAdminSection && !sections.some(s => s.name === 'Super Admin')) {
            sections.push(superAdminSection);
          }
        }

        return sections;
      })
    );
    this.remainingTime$ = this.sessionService.getRemainingTime();
  }

  ngOnInit() {
    this.isOnProfilePage = this.router.url.startsWith('/profile');
  }

  isOpportunitiesActive(): boolean {
    return this.router.url.startsWith('/opportunity');
  }

  isAdminActive(): boolean {
    return this.router.url.startsWith('/admin');
  }

  async handleHeaderAction() {
    if (this.isOnProfilePage) {
      const { data } = await this.modalService.openConfirmationModal({
        title: 'LOGOUT',
        message: 'Are you sure you want to log out?',
        confirmText: 'LOGOUT',
        cancelText: 'CANCEL'
      });

      if (data && data.confirmed) {
        this.logout();
      }
    } else {
      this.navigateTo('/profile');
    }
  }

  navigateTo(url: string, filter?: string) {
    this.filterStateService.clearFilters();
    if (filter) {
      this.filterStateService.setOpenToFilter([filter]);
    }
    this.router.navigateByUrl(url);
    this.closeAllMenus();
  }

  selectAdminSection(section?: { name: string; role: string; }) {
    this.closeAllMenus();

    if (!section) {
      this.adminStateService.setCurrentSection('');
      this.adminStateService.setSectionToLoad(null);
      this.router.navigate(['/admin']);
      return;
    }

    this.adminStateService.setSectionToLoad(section);

    if (!this.router.url.startsWith('/admin')) {
      this.router.navigate(['/admin']);
    }
  }

  @HostListener('document:click', ['$event'])
  handleClickOutside(event: Event) {
    const target = event.target as Node;

    const isClickInside = (element: ElementRef | undefined): boolean => {
      return !!element?.nativeElement?.contains(target);
    };

    if (this.isOpportunitiesOpen && !isClickInside(this.dropdownContainer) && !isClickInside(this.mobileOpportunitiesToggle)) {
      this.isOpportunitiesOpen = false;
    }

    if (this.isAdminMenuOpen && !isClickInside(this.adminMenuContainer) && !isClickInside(this.mobileAdminToggle)) {
      this.isAdminMenuOpen = false;
    }

    if (this.isUserMenuOpen && !isClickInside(this.userMenuContainer)) {
      this.isUserMenuOpen = false;
    }

    if (this.isMobileMenuOpen && !isClickInside(this.mobileMenu) && !isClickInside(this.mobileMenuToggle)) {
      this.isMobileMenuOpen = false;
    }
  }

  private closeAllMenus() {
    this.isMobileMenuOpen = false;
    this.isOpportunitiesOpen = false;
    this.isAdminMenuOpen = false;
    this.isUserMenuOpen = false;
  }

  toggleMobileMenu() {
    this.isMobileMenuOpen = !this.isMobileMenuOpen;
    if (this.isMobileMenuOpen) {
      this.isOpportunitiesOpen = false;
      this.isAdminMenuOpen = false;
    }
  }

  toggleOpportunities() {
    this.isOpportunitiesOpen = !this.isOpportunitiesOpen;
    if (this.isOpportunitiesOpen) {
      this.isAdminMenuOpen = false;
      this.isUserMenuOpen = false;
      this.calculateOpportunitiesPosition();
    }
  }

  toggleUserMenu() {
    this.isUserMenuOpen = !this.isUserMenuOpen;
    if (this.isUserMenuOpen) {
      this.isOpportunitiesOpen = false;
      this.isAdminMenuOpen = false;
    }
  }

  toggleAdminMenu() {
    this.isAdminMenuOpen = !this.isAdminMenuOpen;
    if (this.isAdminMenuOpen) {
      this.isOpportunitiesOpen = false;
      this.isUserMenuOpen = false;
      this.calculateAdminPosition();
    }
  }

  calculateOpportunitiesPosition() {
    if (this.dropdownContainer && this.dropdownContainer.nativeElement) {
      const rect = this.dropdownContainer.nativeElement.getBoundingClientRect();
      this.opportunitiesDropdownPosition = {
        left: rect.left + window.scrollX,
        top: rect.bottom + window.scrollY
      };
    }
  }

  calculateAdminPosition() {
    if (this.adminMenuContainer && this.adminMenuContainer.nativeElement) {
      const rect = this.adminMenuContainer.nativeElement.getBoundingClientRect();
      this.adminDropdownPosition = {
        left: rect.left + window.scrollX,
        top: rect.bottom + window.scrollY
      };
    }
  }

  openLoginModal() {
    this.modalService.openLoginModal();
    this.isMobileMenuOpen = false;
  }

  logout() {
    this.authService.logout();
    this.router.navigate(['/']);
    this.closeAllMenus();
  }

  getAdminSectionIcon(sectionName: string): string {
    switch (sectionName) {
      case 'Opportunity': return '&#129309;';
      case 'Student': return '&#127891;';
      case 'Academic Staff': return '&#128104;&#8205;&#127979;';
      case 'Administrative Staff': return '&#128188;';
      case 'Non-University Stakeholder': return '&#129309;';
      case 'Media': return '&#128247;';
      case 'Super Admin': case 'Org Admin': return '&#128187;';
      default: return '&#10067;';
    }
  }
}